import React, { useState, useEffect } from 'react';
import { adminAPI, cityAPI } from '../../services/api';

// Admin management for the home "Featured" carousel custom banner slides.
// These are separate from the paid featured_banner restaurant addon (purely
// additive) and render BEFORE the paid featured restaurants, ordered by position.
const EMPTY_FORM = {
  placement: 'home',
  title: '',
  restaurantId: '',
  isGlobal: true,
  cityId: '',
  position: 0,
  isActive: true,
  startDate: '',
  endDate: '',
};

export default function BannerSlideManagement() {
  const [slides, setSlides] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('home'); // which placement's slides to show
  const [cartPosition, setCartPosition] = useState('above'); // above/below coupons
  const [savingCartPos, setSavingCartPos] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sRes, rRes, cRes, cfgRes] = await Promise.all([
        adminAPI.getBannerSlides(),
        adminAPI.getAllRestaurants({ limit: 500 }).catch(() => ({ data: {} })),
        cityAPI.getActiveCities().catch(() => ({ data: {} })),
        adminAPI.getCartBannerConfig().catch(() => ({ data: {} })),
      ]);
      setSlides(sRes.data?.bannerSlides || []);
      setCartPosition(cfgRes.data?.position === 'below' ? 'below' : 'above');
      // Both endpoints wrap the array under `data` — getAllRestaurants returns
      // { data: { restaurants } } and getActiveCities returns { data: { cities } }.
      // (Reading rRes.data.data alone yielded the wrapper OBJECT, failing the
      // Array.isArray guard → empty dropdowns.)
      const rList = rRes.data?.data?.restaurants || rRes.data?.restaurants || [];
      setRestaurants(Array.isArray(rList) ? rList : []);
      const cList = cRes.data?.data?.cities || cRes.data?.cities || [];
      setCities(Array.isArray(cList) ? cList : []);
    } catch (e) {
      setError('Failed to load banner slides');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, placement: tab }); // new slide defaults to the active tab
    setImageFile(null);
    setImagePreview('');
    setError('');
    setShowForm(true);
  };

  const saveCartPosition = async (pos) => {
    setCartPosition(pos);
    setSavingCartPos(true);
    try {
      await adminAPI.updateCartBannerConfig(pos);
    } catch (e) {
      alert('Failed to save cart banner position');
    } finally {
      setSavingCartPos(false);
    }
  };

  const openEdit = (s) => {
    setEditingId(s._id);
    setForm({
      placement: s.placement === 'cart' ? 'cart' : 'home',
      title: s.title || '',
      restaurantId: s.restaurantId?._id || s.restaurantId || '',
      isGlobal: s.isGlobal !== false,
      cityId: s.cityId?._id || s.cityId || '',
      position: s.position || 0,
      isActive: s.isActive !== false,
      startDate: s.startDate ? s.startDate.slice(0, 10) : '',
      endDate: s.endDate ? s.endDate.slice(0, 10) : '',
    });
    setImageFile(null);
    setImagePreview(s.imageUrl || '');
    setError('');
    setShowForm(true);
  };

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const handleSubmit = async () => {
    setError('');
    if (!editingId && !imageFile) { setError('Please choose an image'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      if (imageFile) fd.append('image', imageFile);
      fd.append('placement', form.placement === 'cart' ? 'cart' : 'home');
      fd.append('title', form.title || '');
      fd.append('restaurantId', form.restaurantId || '');
      fd.append('isGlobal', form.isGlobal ? 'true' : 'false');
      fd.append('cityId', form.isGlobal ? '' : (form.cityId || ''));
      fd.append('position', String(Number(form.position) || 0));
      fd.append('isActive', form.isActive ? 'true' : 'false');
      fd.append('startDate', form.startDate || '');
      fd.append('endDate', form.endDate || '');

      if (editingId) {
        await adminAPI.updateBannerSlide(editingId, fd);
      } else {
        await adminAPI.createBannerSlide(fd);
      }
      setShowForm(false);
      await loadAll();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save banner slide');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm('Delete this banner slide? This cannot be undone.')) return;
    try {
      await adminAPI.deleteBannerSlide(s._id);
      await loadAll();
    } catch (e) {
      alert('Failed to delete banner slide');
    }
  };

  const restName = (s) => s.restaurantId?.name || restaurants.find(r => r._id === (s.restaurantId?._id || s.restaurantId))?.name;

  const visibleSlides = slides.filter((s) => (s.placement === 'cart' ? 'cart' : 'home') === tab);

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0F172A' }}>🖼️ Banner Slides</h1>
          <p style={{ margin: '6px 0 0', color: '#64748B', fontSize: 14 }}>
            {tab === 'home'
              ? 'Custom images shown in the home Featured carousel (before paid featured restaurants).'
              : 'Custom images shown on the cart screen, around the coupon & offers section.'}
          </p>
        </div>
        <button onClick={openAdd} style={btn('#2563EB')}>+ Add {tab === 'cart' ? 'Cart' : 'Home'} Slide</button>
      </div>

      {/* Placement tabs — home vs cart slides are separate sets. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['home', '🏠 Home carousel'], ['cart', '🛒 Cart screen']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            background: tab === k ? '#2563EB' : '#fff', color: tab === k ? '#fff' : '#334155',
            border: '1px solid ' + (tab === k ? '#2563EB' : '#E2E8F0'), borderRadius: 8,
            padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>{label} ({slides.filter((s) => (s.placement === 'cart' ? 'cart' : 'home') === k).length})</button>
        ))}
      </div>

      {/* Cart-only: choose where the banner renders relative to the coupon section. */}
      {tab === 'cart' && (
        <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 12, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: '#0C4A6E', fontSize: 14 }}>Show the cart banner:</span>
          {[['above', '⬆️ Above coupon & offers'], ['below', '⬇️ Below coupon & offers']].map(([k, label]) => (
            <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontSize: 14, color: '#0C4A6E', fontWeight: cartPosition === k ? 700 : 400 }}>
              <input type="radio" name="cartpos" checked={cartPosition === k} onChange={() => saveCartPosition(k)} disabled={savingCartPos} />
              {label}
            </label>
          ))}
          {savingCartPos && <span style={{ color: '#64748B', fontSize: 13 }}>Saving…</span>}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#64748B' }}>Loading…</p>
      ) : visibleSlides.length === 0 ? (
        <div style={{ background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 12, padding: 40, textAlign: 'center', color: '#64748B' }}>
          No {tab === 'cart' ? 'cart' : 'home'} banner slides yet. Click “Add {tab === 'cart' ? 'Cart' : 'Home'} Slide” to upload your first promotional image.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {visibleSlides.map((s) => (
            <div key={s._id} style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ position: 'relative', height: 150, background: '#F1F5F9' }}>
                {s.imageUrl ? <img src={s.imageUrl} alt={s.title || 'banner'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                {!s.isActive && <span style={badge('#EF4444')}>Inactive</span>}
              </div>
              <div style={{ padding: 12 }}>
                <div style={{ fontWeight: 600, color: '#0F172A', marginBottom: 6 }}>{s.title || <span style={{ color: '#94A3B8' }}>(no title)</span>}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  <span style={chip()}>{s.isGlobal !== false ? '🌐 All cities' : `🏙️ ${s.cityId?.name || 'City'}`}</span>
                  <span style={chip()}>#{s.position || 0}</span>
                  {restName(s) ? <span style={chip('#EEF2FF', '#4338CA')}>🔗 {restName(s)}</span> : <span style={chip('#F1F5F9', '#64748B')}>No link</span>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openEdit(s)} style={btn('#0EA5E9', true)}>Edit</button>
                  <button onClick={() => handleDelete(s)} style={btn('#EF4444', true)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={overlay} onClick={() => !saving && setShowForm(false)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, fontSize: 20 }}>{editingId ? 'Edit Banner Slide' : 'Add Banner Slide'}</h2>

            <label style={lbl}>Where to show this slide</label>
            <select style={inp} value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })}>
              <option value="home">🏠 Home carousel</option>
              <option value="cart">🛒 Cart screen (around coupon &amp; offers)</option>
            </select>

            <label style={lbl}>Image {editingId ? '(leave empty to keep current)' : '*'}</label>
            <input type="file" accept="image/*" onChange={onPickImage} />
            {imagePreview ? <img src={imagePreview} alt="preview" style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, marginTop: 8 }} /> : null}

            <label style={lbl}>Title / caption (optional)</label>
            <input style={inp} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Diwali Offer" maxLength={80} />

            <label style={lbl}>Link to a restaurant (optional — tapping the slide opens it)</label>
            <select style={inp} value={form.restaurantId} onChange={(e) => setForm({ ...form, restaurantId: e.target.value })}>
              <option value="">No link (not tappable)</option>
              {restaurants.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>

            <label style={lbl}>Scope</label>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 4 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={form.isGlobal} onChange={(e) => setForm({ ...form, isGlobal: e.target.checked })} />
                Show in all cities (global)
              </label>
            </div>
            {!form.isGlobal && (
              <select style={inp} value={form.cityId} onChange={(e) => setForm({ ...form, cityId: e.target.value })}>
                <option value="">Select a city…</option>
                {cities.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Position (lower = first)</label>
                <input style={inp} type="number" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: 12 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  Active
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Start date (optional)</label>
                <input style={inp} type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>End date (optional)</label>
                <input style={inp} type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
              </div>
            </div>

            {error ? <p style={{ color: '#EF4444', fontSize: 14 }}>{error}</p> : null}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setShowForm(false)} disabled={saving} style={btn('#94A3B8')}>Cancel</button>
              <button onClick={handleSubmit} disabled={saving} style={btn('#2563EB')}>{saving ? 'Saving…' : (editingId ? 'Save' : 'Create')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btn = (bg, small) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: small ? '6px 12px' : '10px 16px', fontSize: small ? 13 : 14, fontWeight: 600, cursor: 'pointer' });
const badge = (bg) => ({ position: 'absolute', top: 8, right: 8, background: bg, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6 });
const chip = (bg = '#F1F5F9', color = '#334155') => ({ background: bg, color, fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6 });
const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', margin: '12px 0 4px' };
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 1000, overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 520, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
